/**
 * Time-bound a promise. If the promise resolves before the timeout, its value is returned. Otherwise,
 * the fallback function is called and its result is returned instead. The success function is called if
 * the promise resolves before the timeout, but it does not impact the return value.
 *
 * @param promise the promise to time-bound
 * @param success a function to call if the promise resolves before the timeout
 * @param fallback a function to call if the promise does not resolve before the timeout
 * @param timeout the timeout in milliseconds
 * @returns a time-bound promise
 */
export default function timeBound<T>(
  promise: Promise<T>,
  success: () => void,
  fallback: () => Promise<T>,
  timeout: number
): Promise<T> {
  let timedOut = false;
  let timeoutId: ReturnType<typeof setTimeout>;

  const timeoutPromise = new Promise<void>((resolve) => {
    timeoutId = setTimeout(() => {
      timedOut = true;
      resolve();
    }, timeout);
  });

  const markInvoked = (): void => {
    clearTimeout(timeoutId);
  };

  const succeed = (value: T): T => {
    success();
    return value;
  };

  // If we timed out, we want to return the result of the fallback function instead of the promise result.
  // Otherwise, we want to record success (for timing purposes) and return the promise result.
  const finish = (result: T | void): T | Promise<T> => (timedOut ? fallback() : succeed(result as T));

  return Promise.race([promise.finally(markInvoked), timeoutPromise]).then(finish);
}
